import type { WorkflowDefinition } from "../types";

/**
 * W1 — Baseline a brand's AI visibility.
 *
 * Organization-scoped because it starts before the property exists: step 1 is
 * creating (or bulk-adding) the subject, and the run binds to whatever property
 * that produces. `perTarget` so two people onboarding two clients in the same
 * hour each get their own run instead of fighting over one checklist.
 *
 * Live since PR 6, which built the org library and the `AddPropertyDialog`
 * binding hand-off: the run starts unbound from the organization's Workflows
 * page and binds to the property step 1 creates.
 */
export const visibilityBaselineWorkflow: WorkflowDefinition = {
	id: "visibility-baseline",
	wNumber: 1,
	title: "Baseline your brand's AI visibility",
	outcome:
		"Get a clear picture of what AI says about the brand today, ready to share.",
	audiences: ["agencies", "pr", "inhouse"],
	uses: ["Properties", "AI Reports", "Brand Snapshot", "Sharing"],
	scope: "organization",
	bindsProperty: true,
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "property", label: "Brand" },
	// Every subject that gets a dashboard can be baselined, CATEGORY included:
	// W1 is the run-first-report path, and a market's baseline is the competitor
	// breakout. LOCATION children inherit their parent's baseline.
	isApplicable: (mode) =>
		mode.isBrand ||
		mode.isProduct ||
		mode.isCategory ||
		mode.subjectType === "PERSON",
	articleSlug: "how-to-baseline-your-brands-ai-visibility",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "add-property",
			title: "Add the brand you want to measure",
			page: "Organization dashboard",
			route: "org:/",
			criterion: "You've added the brand.",
			why: "Everything else hangs off the brand: its prompts, competitors and reports. Still pitching to win the work? Add them as a prospect instead.",
			docsHref: "/docs/getting-started/overview",
			tasks: [
				"Add the brand with its main website address",
				"Or bulk-add a list of prospects you are pitching",
			],
			complete: { kind: "bound" },
			// Completion is the run binding to a property, not the
			// organization's property count — an agency with sixty clients
			// has not "done" this step for the sixty-first.
			signal: ({ run }) => ({
				kind: "checklist",
				items: [
					{
						label: "Brand added",
						done: Boolean(run?.propertyId),
					},
				],
			}),
		},
		{
			id: "run-report",
			title: "Start an AI Visibility Report",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've completed one report.",
			why: "The report is your starting point. It shows how AI describes the brand and how it compares with rivals.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Start a report and wait 4 to 15 minutes",
				"Pick a location if answers change by country",
			],
			complete: { kind: "count", key: "completedReportCount", gte: 1 },
			spend: { kind: "aiReport" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Completed reports",
							value: state.property.completedReportCount,
							target: 1,
						}
					: null,
		},
		{
			id: "review-snapshot",
			title: "Check and correct the Brand Snapshot",
			page: "Brand Snapshot",
			route: "/brand-snapshot",
			criterion: "You've corrected at least one field.",
			why: "This is what the brand says about itself. Get it wrong and every later number measures the wrong company.",
			docsHref: "/docs/dashboards/brand-snapshot",
			tasks: [
				"Check the name, category and audience against the brand's own words",
				"Fill in what makes them different and the problems they solve",
				"Fix anything the crawl got wrong",
			],
			complete: {
				kind: "count",
				key: "snapshotOverrideFieldCount",
				gte: 1,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Fields you corrected",
							value: state.property.snapshotOverrideFieldCount,
							target: 1,
							caption:
								"your edits are kept when the snapshot is rebuilt",
						}
					: null,
		},
		{
			id: "read-report",
			title: "Read the report end to end",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've read the report.",
			why: "The numbers only become a story once someone reads them. This is the half hour that turns a report into a pitch.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Read the summary, then the breakdown by AI platform",
				"Note two or three questions where the brand is missing",
				"Skim the recommendations for quick wins",
			],
			complete: { kind: "manual" },
		},
		{
			id: "share-report",
			title: "Share the report",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've exported or shared the report.",
			why: "The link, PDF and slides all carry your branding. Sharing is what turns the work into a deliverable.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Export the PDF or slides, or copy the share link",
				"Send it with the two or three findings you want to discuss",
			],
			complete: { kind: "since", event: "reportExported" },
			signal: ({ state }) => ({
				kind: "checklist",
				items: [
					{
						label: "Your branding on the shared report",
						done: state.organization.hasWhiteLabelBranding,
					},
				],
			}),
		},
	],
};
